import JsonLd from '@allesauto/core/JsonLd';
import translate from '@allesauto/i18n';
import { verifyVehicleDate } from '@allesauto/utils/funcs';
import {
  Calendar24,
  ColorPalette24,
  GasStation24,
  Meter24,
  Road24,
} from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Car } from 'schema-dts';

export interface MileageProps {
  measurements: string;
  value: number;
}

export interface CarMainDetailsProps {
  carManufacturer: string;
  carModel: string;
  engineType: string;
  equipment: string;
  mileage: number;
  registrationDate?: string;
  fuelType: string;
  powerKw: number;
  powerHp: number;
  trim: string;
  bodyColor: string;
}

const CarMainDetails: FC<React.PropsWithChildren<CarMainDetailsProps>> = ({
  carManufacturer,
  carModel,
  engineType,
  equipment,
  mileage,
  registrationDate,
  fuelType,
  powerKw,
  powerHp,
  trim,
  bodyColor,
}: CarMainDetailsProps) => {
  const intl = useIntl();

  const [showLeftOverlay, setShowLeftOverlay] = useState(false);
  const [showRightOverlay, setShowRightOverlay] = useState(true);

  const meta: Car = useMemo(
    () => ({
      '@type': 'Car',
      fuelType,
      manufacturer: carManufacturer,
      model: carModel,
      mileageFromOdometer: {
        '@type': 'QuantitativeValue',
        value: mileage,
        unitCode: 'km',
      },
      dateVehicleFirstRegistered: registrationDate,
      description: equipment,
      vehicleEngine: {
        '@type': 'EngineSpecification',
        enginePower: {
          '@type': 'QuantitativeValue',
          value: powerKw,
          unitCode: 'kW',
        },
        engineType,
      },
    }),
    [
      carManufacturer,
      carModel,
      engineType,
      equipment,
      fuelType,
      mileage,
      powerKw,
      registrationDate,
    ]
  );

  const carDetails = [
    {
      text: translate(intl, 'ui.UNITS.MILEAGE', {
        value: intl.formatNumber(mileage),
      }),
      icon: <Meter24 />,
    },
    {
      text: verifyVehicleDate(registrationDate),
      icon: <Calendar24 />,
    },
    {
      text: fuelType,
      icon: <GasStation24 />,
    },
    {
      text: translate(intl, 'ui.UNITS.POWER_KW_HP', {
        valueKW: intl.formatNumber(powerKw),
        valueHP: intl.formatNumber(powerHp),
      }),
      icon: <Road24 />,
    },
    {
      text: bodyColor,
      icon: <ColorPalette24 />,
    },
  ];

  const swipperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const instanceSwipper = swipperRef.current;
    const listenToScrollHorizontally = () => {
      const scrollLeft = instanceSwipper ? instanceSwipper.scrollLeft : 0;
      const scrollRight = instanceSwipper
        ? instanceSwipper.offsetWidth + instanceSwipper.scrollLeft
        : 0;

      const scrollWidth = instanceSwipper ? instanceSwipper?.scrollWidth : 0;

      setShowLeftOverlay(scrollLeft > 0);
      setShowRightOverlay(!(scrollRight >= scrollWidth));
    };
    if (instanceSwipper) {
      instanceSwipper.addEventListener('scroll', listenToScrollHorizontally);
      return () =>
        instanceSwipper.removeEventListener(
          'scroll',
          listenToScrollHorizontally
        );
    }
  }, [swipperRef, showLeftOverlay]);

  const swipperClassName = {
    leftOverlay: clsx(
      '-left-px absolute h-full swipper-left-overlay top-0 w-35px z-10',
      showLeftOverlay && 'block',
      !showLeftOverlay && 'hidden'
    ),
    rightOverlay: clsx(
      '-right-px absolute h-full swipper-right-overlay top-0 w-35px z-10',
      showRightOverlay && 'block',
      !showRightOverlay && 'hidden'
    ),
  };

  const maxLength = 37;
  const connectedTitleMakeAndModel = `${carManufacturer?.replace(
    /_/g,
    ''
  )} ${carModel?.replace(/_/g, ' ')}`;
  const shortenedTitle =
    connectedTitleMakeAndModel.length > maxLength
      ? connectedTitleMakeAndModel.substring(0, maxLength - 3) + '...'
      : connectedTitleMakeAndModel;

  return (
    <>
      <JsonLd value={meta} />
      <div className="sm:col-end-24 sm:col-start-2 md:col-end-8 md:col-start-2 md:flex md:flex-1 md:flex-col">
        <div className="sm:pt-15px md:pt-20px">
          <h1
            className="font-headline text-headline2 text-purple80 monochrome:text-black"
            id="label--vehicle-name"
            data-style-for-print="main-details-heading"
          >
            {shortenedTitle}
          </h1>
          <p className="text-grey100 text-p1 sm:pb-5px sm:pt-5px md:pb-0 md:pt-15px">
            {trim}
          </p>
        </div>
        <div className="mt-auto sd:pd-[223px] w-[66%] sm:hidden md:block md:pb-0">
          {carDetails.map(
            (carInfo, index) =>
              carInfo.text && (
                <p
                  key={index}
                  className="border-b-1px border-grey30 flex flex-row h-[60px] items-center text-grey100 last:border-b-0"
                >
                  {carInfo.icon}
                  <span
                    className="font-weight-bold pl-[28px] w-full"
                    id={`label--car-${index}`}
                  >
                    {carInfo.text}
                  </span>
                </p>
              )
          )}
        </div>
      </div>
      <div className="block col-end-24 col-start-2 order-1 relative md:hidden">
        <div className={swipperClassName.leftOverlay} />
        <div
          ref={swipperRef}
          className="border-y-1px border-y-grey30 flex flex-1 hide-scrollbar mt-15px swipper-wrapper sm:overflow-x-scroll md:hidden"
        >
          {carDetails.map(
            (carInfo, index) =>
              carInfo.text && (
                <div
                  key={index}
                  className="flex items-center mr-35px py-20px shrink-0 last:mr-0"
                >
                  {carInfo.icon}
                  <span className="font-weight-bold pl-[12px] text-grey100 text-headline4 whitespace-nowrap">
                    {carInfo.text}
                  </span>
                </div>
              )
          )}
        </div>
        <div className={swipperClassName.rightOverlay} />
      </div>
    </>
  );
};

export default CarMainDetails;
